import React, { useRef, useState } from 'react'

import PageLayout from 'layouts/PageLayout'
import Banner from 'containers/Banner'
import {
  Container,
  SidebarLeftContainer,
  RightSidebarContainer,
  BannerWrapper,
} from './SidebarLayout.styles'
import DisableBodyScroll from 'components/DisableBodyScroll'
import { useForTablet } from 'components/Breakpoint'
import useOnClickOutside from 'utils/hooks/useOnClickOutside'

import { callIfFunction, RenderFunctionOrNode } from '../utils'

/**
 * TYPES
 */
interface SidebarLayoutProps {
  title?: string
  /** Navigation sidebar */
  sidebarLeft?: RenderFunctionOrNode
  /** Activity/inspector sidebar */
  sidebarRight?: RenderFunctionOrNode
  header: RenderFunctionOrNode
  footer?: RenderFunctionOrNode
  children?: RenderFunctionOrNode
  isSidebarRightOpen?: boolean
  toggleSidebarRight?: () => void
  headerHeight?: number
  darkBackground?: boolean
}

/**
 * COMPONENT
 * Used for document/artboard view, it provides a right sidebar to show
 * activity/inspector, also provides the left sidebar for nav (as an overlay)
 */
const SidebarLayout: React.FC<SidebarLayoutProps> = props => {
  const {
    title,
    sidebarLeft,
    sidebarRight,
    header,
    footer,
    toggleSidebarRight,
    children,
    darkBackground,
  } = props

  // If there's no way to render the right sidebar (i.e. it's null), then
  // ensure it can never be set as open
  const isSidebarRightOpen =
    props.sidebarRight === null ? false : props.isSidebarRightOpen

  const [isSidebarLeftOpen, setSidebarLeftOpen] = useState(false)

  const passedProps = {
    isSidebarRightOpen,
    toggleSidebarRight,
    isSidebarLeftOpen,
    setSidebarLeftOpen,
  }

  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  // Close the left sidebar when is opened and clicked outside
  const sidebarLeftRef = useRef<HTMLElement>(null)
  const closeSidebar = () => {
    if (isSidebarLeftOpen) {
      // setTimeout is needed to make sure `setSidebarLeftOpen` is called after
      // any other click event listener, for example the hamburger icon click
      setTimeout(() => setSidebarLeftOpen(false), 0)
    }
  }
  useOnClickOutside(sidebarLeftRef, closeSidebar)

  return (
    <PageLayout title={title} header={callIfFunction(header, passedProps)}>
      {({ headerHeight }) => (
        <>
          {sidebarLeft && (
            <SidebarLeftContainer
              data-testid="side-bar-left"
              ref={sidebarLeftRef}
              isSidebarLeftOpen={isSidebarLeftOpen}>
              {sidebarLeft}
            </SidebarLeftContainer>
          )}
          <Container
            isSidebarLeftOpen={isSidebarLeftOpen}
            isSidebarRightOpen={isSidebarRightOpen}
            darkBackground={darkBackground}>
            <BannerWrapper>
              <Banner />
            </BannerWrapper>
            {callIfFunction(children, passedProps)}
            {callIfFunction(footer, passedProps)}
          </Container>
          {sidebarRight && (
            <RightSidebarContainer
              data-testid="side-bar-right"
              style={{ top: `${headerHeight}px` }}
              isOpen={isSidebarRightOpen}
              darkBackground={darkBackground}>
              {callIfFunction(sidebarRight, passedProps)}
            </RightSidebarContainer>
          )}
          {isSmallerThanTablet && isSidebarRightOpen && <DisableBodyScroll />}
        </>
      )}
    </PageLayout>
  )
}

export { SidebarLayout }
